import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiUser, FiFileText, FiImage, FiTool, FiDollarSign, FiGithub, FiRefreshCw, FiHome, FiBox, FiLayers } from 'react-icons/fi';

const COLORS = {
  primary: {
    light: 'from-emerald-400 to-emerald-500',
    dark: 'from-emerald-600 to-emerald-700', 
    text: 'text-emerald-500',
    hover: 'hover:text-emerald-400',
    glow: 'rgba(16, 185, 129, 0.15)',
  },
  background: 'bg-white',
  border: 'border-gray-100',
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Check if modal is open to prevent scroll effects
  const isModalOpen = () => {
    return document.querySelector('[aria-modal="true"]') !== null;
  };

  useEffect(() => {
    const handleScroll = () => {
      // Don't update scroll position if modal is open
      if (!isModalOpen()) {
        setScrollPosition(window.pageYOffset);
      }
    };
    window.addEventListener('scroll', handleScroll);

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsToolsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { 
      name: 'Home', 
      path: '/', 
      icon: <FiHome className="stroke-[1.5]" />,
      description: 'Dashboard & Overview'
    },
    { 
      name: 'Unit Conversions', 
      path: '/unit-conversions', 
      icon: <FiRefreshCw className="stroke-[1.5]" />,
      description: 'Precision Calculations'
    },
  ];

  // Tools dropdown items - all conversion tools
  const toolsDropdownItems = [
    { 
      name: 'Images to PDF', 
      path: '/images-to-pdf', 
      icon: <FiImage className="stroke-[1.5]" />,
      description: 'Convert multiple images to PDF',
      category: 'Image Tools'
    },
    { 
      name: 'PDF to Images', 
      path: '/pdf-to-images', 
      icon: <FiLayers className="stroke-[1.5]" />,
      description: 'Extract images from PDF',
      category: 'Image Tools'
    },
    { 
      name: 'PDF to Word', 
      path: '/convert/pdf-to-word', 
      icon: <FiFileText className="stroke-[1.5]" />,
      description: 'Convert PDF to editable Word',
      category: 'Document Tools'
    },
    { 
      name: 'Word to PDF', 
      path: '/convert/word-to-pdf', 
      icon: <FiFileText className="stroke-[1.5]" />,
      description: 'Convert Word to PDF',
      category: 'Document Tools'
    },
    { 
      name: 'All File Converters', 
      path: '/', 
      icon: <FiBox className="stroke-[1.5]" />,
      description: 'View all conversion tools',
      category: 'All Tools',
      highlight: true
    },
  ];

  const conversionOptions = [
    { 
      name: 'Images to PDF', 
      path: '/images-to-pdf', 
      icon: <FiImage className="stroke-[1.5]" />,
      description: 'High-quality Image Conversion'
    },
    { 
      name: 'PDF to Images', 
      path: '/pdf-to-images', 
      icon: <FiLayers className="stroke-[1.5]" />,
      description: 'Extract Images from PDFs'
    },
  ];

  // Tools Dropdown Component
  const ToolsDropdown = () => {
    return (
      <div className="relative" ref={dropdownRef}>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
          className={`
            relative px-4 py-2 rounded-lg text-sm font-medium
            flex items-center gap-2.5 transition-all duration-300
            ${location.pathname.startsWith('/convert') || location.pathname.startsWith('/images') || location.pathname.startsWith('/pdf-to')
              ? 'text-primary-600 dark:text-primary-400 bg-primary-50/80 dark:bg-primary-900/40' 
              : 'text-gray-800 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50/50 dark:hover:bg-gray-800/70'}
          `}
        >
          <FiBox className="stroke-[1.5]" />
          <span>Tools</span>
          <motion.span
            animate={{ rotate: isToolsDropdownOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-xs"
          >
            ▼
          </motion.span>
          <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-medium
                      bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-700 dark:to-primary-800 text-white rounded-full
                      shadow-sm shadow-primary-500/20" aria-hidden="true">
            New
          </span>
        </motion.button>

        <AnimatePresence>
          {isToolsDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 overflow-hidden z-50"
            >
              <div className="py-2">
                {toolsDropdownItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={() => setIsToolsDropdownOpen(false)}
                    className={`
                      block px-4 py-2.5 text-sm transition-colors duration-200
                      ${item.highlight
                        ? 'bg-primary-50/50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-gray-700 dark:text-neutral-300 hover:bg-primary-50/50 dark:hover:bg-gray-800/70 hover:text-primary-600 dark:hover:text-primary-400'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{item.icon}</span>
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {item.description}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const NavLink = ({ item, highlight = false }) => {
    const location = useLocation();
    const isActive = location.pathname === item.path;
    
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="relative group"
      >
        <Link
          to={item.path}
          className={`
            group relative px-4 py-2 rounded-lg text-sm font-medium
            flex items-center gap-2.5 transition-all duration-300
            ${isActive 
              ? 'text-primary-600 dark:text-primary-400 bg-primary-50/80 dark:bg-primary-900/40' 
              : 'text-gray-800 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50/50 dark:hover:bg-gray-800/70'}
          `}
        >
          <span className={`
            relative text-lg transition-all duration-300
            ${isActive ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 dark:text-neutral-400 group-hover:text-primary-500 dark:group-hover:text-primary-400'}
          `}>
            <motion.div
              whileHover={{ rotate: 15 }}
              className="relative z-10"
            >
              {item.icon}
            </motion.div>
            <div className="absolute inset-0 bg-primary-400/10 blur-lg 
                         opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </span>
          
          <span>{item.name}</span>
          
          {item.badge && (
            <motion.span 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-medium
                      bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-700 dark:to-primary-800 text-white rounded-full
                      shadow-sm shadow-primary-500/20"
            >
              {item.badge}
            </motion.span>
          )}

          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 
                       opacity-0 group-hover:opacity-100 transition-opacity duration-200
                       pointer-events-none z-50">
            <div className="bg-gray-900 dark:bg-gray-800 text-white dark:text-neutral-100 text-xs px-2 py-1 rounded-md whitespace-nowrap border border-gray-800 dark:border-gray-700">
              {item.description}
            </div>
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 
                         border-4 border-transparent border-b-gray-900 dark:border-b-gray-800" />
          </div>
        </Link>
      </motion.div>
    );
  };

  const Logo = () => (
    <Link to="/">
      <motion.div 
        className="flex items-center gap-3.5 group"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        {/* Logo Icon Container */}
        <div className="relative">
          <motion.div
            className="w-8 h-8 relative z-10"
            whileHover={{ 
              rotate: 360,
              transition: { duration: 0.7, ease: "easeInOut" }
            }}
          >
            {/* Primary Icon */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-500 
                          rounded-xl shadow-lg transform -rotate-3 transition-transform 
                          group-hover:rotate-3 duration-300">
              <img
                src="/logo.svg"
                alt="iconvertify icon"
                className="w-full h-full p-1.5 drop-shadow-md"
              />
            </div>
            
            {/* Enhanced Decorative Elements */}
            <div className="absolute -inset-0.5 bg-gradient-to-br from-primary-200 to-primary-400 
                          opacity-50 blur-md rounded-xl group-hover:opacity-75 transition-opacity" />
            <div className="absolute -inset-1 bg-gradient-to-br from-primary-300/20 to-primary-500/20 
                          blur-xl rounded-full group-hover:blur-2xl transition-all" />
          </motion.div>
        </div>
        
        {/* Brand Text with enhanced gradient */}
        <div className="flex flex-col items-start -space-y-0.5">
          <div className="flex items-baseline gap-0.5">
            <span className="text-lg font-['Inter'] tracking-tight">
              <span className={`font-extrabold text-gray-900 dark:text-neutral-100`}>icon</span>
              <span className={`font-normal bg-gradient-to-r from-primary-600 via-primary-500 to-primary-400 bg-clip-text text-transparent transition-all duration-300
                dark:from-primary-400 dark:via-primary-300 dark:to-primary-200`}>vertify</span>
            </span>
            <span className="text-[0.65rem] text-primary-400/40 dark:text-primary-300/60 font-light">beta</span>
          </div>
          
          {/* Enhanced Tagline Animation */}
          <motion.span 
            className="text-[0.65rem] text-gray-400/80 dark:text-neutral-400 font-normal tracking-wider uppercase
                     transform origin-left transition-all duration-300
                     group-hover:text-primary-400/90"
            whileHover={{ scale: 1.02 }}
          >
            Convert Anything
          </motion.span>
        </div>

        {/* Enhanced decorative dots */}
        <div className="absolute -top-1 -right-1 flex space-x-0.5">
          <motion.div 
            className="w-0.5 h-0.5 rounded-full bg-primary-400/50"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.div 
            className="w-0.5 h-0.5 rounded-full bg-primary-400/30"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
          />
          <motion.div 
            className="w-0.5 h-0.5 rounded-full bg-primary-400/20"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
          />
        </div>
      </motion.div>
    </Link>
  );

  const PrivacyButton = () => (
    <Link to="/privacy">
      <motion.button
        whileHover={{ 
          scale: 1.02,
          boxShadow: "0 8px 20px -3px rgba(16, 185, 129, 0.15)"
        }}
        whileTap={{ scale: 0.98 }}
        className="relative group overflow-hidden inline-flex items-center px-4 py-2
                  rounded-lg text-sm font-medium 
                  bg-gradient-to-br from-primary-600 to-primary-500
                  text-white shadow-sm transition-all duration-300"
      >
        {/* Subtle glow effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/0 via-primary-500/10 to-primary-500/0 
                      opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
        
        {/* Icon with animation */}
        <motion.div
          className="mr-2 relative z-10"
          whileHover={{ rotate: 15 }}
        >
          <FiFileText className="text-lg" />
        </motion.div>

        <span className="relative z-10">Privacy</span>
        
        {/* Animated shine effect */}
        <div className="absolute inset-0 rounded-lg overflow-hidden">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
            initial={{ x: '-100%' }}
            whileHover={{ x: '100%' }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </motion.button>
    </Link>
  );

  return (
    <nav
      className="fixed top-0 left-0 w-full z-50 transition-all duration-300
        bg-white/90 dark:bg-gray-950/95 border-b border-gray-100 dark:border-gray-800
        shadow-sm dark:shadow-none backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Logo />
          </div>
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-1">
              {navItems.map((item, index) => (
                <NavLink key={index} item={item} />
              ))}
              <ToolsDropdown />
              <div className="relative mx-2 flex items-center">
                <div className="w-px h-6 bg-gradient-to-b from-transparent via-gray-200/80 dark:via-gray-700/80 to-transparent" />
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-b from-primary-400/0 via-primary-400/20 to-primary-400/0"
                  animate={{ 
                    opacity: [0.3, 0.6, 0.3],
                    height: ['0%', '100%', '0%']
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>
              {conversionOptions.map((item, index) => (
                <NavLink key={`conversion-${index}`} item={item} highlight={true} />
              ))}
            </div>
          </div>
          <div className="hidden md:block">
            <div className="flex items-center space-x-4">
              <motion.a
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-1.5 rounded-lg text-sm font-medium 
                  flex items-center gap-2 transition-all duration-200 hover:bg-primary-50/50 dark:hover:bg-gray-800/70
                  relative group overflow-hidden"
              >
                <FiGithub className="text-lg" />
                <span>GitHub</span>
                <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-primary-50/0 to-primary-100/0 
                              opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </motion.a>
              <PrivacyButton />
            </div>
          </div>
          <div className="-mr-2 flex md:hidden">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="relative group inline-flex items-center justify-center p-2 rounded-lg
                        text-gray-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 
                        overflow-hidden transition-colors dark:hover:bg-gray-800/70"
            >
              <span className="sr-only">Toggle menu</span>
              <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-primary-100/50 
                            opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {isOpen ? (
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 90 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiX className="w-5 h-5" />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ rotate: -90 }}
                  animate={{ rotate: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiMenu className="w-5 h-5" />
                </motion.div>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/10 backdrop-blur-sm md:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Mobile Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 md:hidden bg-white dark:bg-gray-950 border-b border-gray-100 dark:border-gray-800 shadow-lg"
            >
              {/* Navigation Links */}
              <div className="px-4 pt-3 pb-4 space-y-1">
                {navItems.map((item, index) => (
                  <NavLink key={index} item={item} />
                ))}
                
                {/* Separator */}
                <div className="my-2 border-t border-gray-100 dark:border-gray-800" />
                
                {/* Tools Section */}
                <div className="space-y-1">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Conversion Tools
                  </div>
                  {toolsDropdownItems.map((item, index) => (
                    <NavLink key={`tool-${index}`} item={item} highlight={item.highlight} />
                  ))}
                </div>
              </div>

              {/* User Section */}
              <div className="border-t border-gray-100 dark:border-gray-800">
                {/* Guest Actions */}
                <div className="px-4 py-4 space-y-3">
                  <Link
                    to="/github"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-600 dark:text-neutral-400 hover:text-emerald-600 dark:hover:text-primary-400 hover:bg-emerald-50/50 dark:hover:bg-gray-800/70 transition-colors"
                  >
                    <FiGithub className="text-lg" />
                    <span className="text-sm font-medium">GitHub</span>
                  </Link>
                  <PrivacyButton />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;